'use client';

import BaseSpinner from '@module/components/Spinner/BaseSpinner';
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { cn } from '@module/lib/utils';
import { useASPStore } from '@module/stores/useASPStore';
import useModalStore from '@module/stores/useModalStore';
import useOldPlayerStore from '@module/stores/useOldPlayerStore';
import type { MODAL_TYPE_PC } from '@module/types/modal/type';
import dynamic from 'next/dynamic';
import React, { type ComponentType, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const MODAL_COMPONENTS: Partial<Record<keyof typeof MODAL_TYPE_PC, ComponentType<any>>> = {
  CONFIRM_MODAL: dynamic(() => import('./confirm/ConfirmModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  CONFIRM_CANCEL_MODAL: dynamic(() => import('./confirm/ConfirmCancelModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  INPUT_MODAL: dynamic(() => import('./input/InputModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  GIFT_HEART_MODAL: dynamic(() => import('./gift/GiftHeartModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  SEND_ITEM_MODAL: dynamic(() => import('./gift/SendItemModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  GIFTABLE_ITEM_MODAL: dynamic(() => import('./gift/GiftableItemModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  SEND_MESSAGE_MODAL: dynamic(() => import('./message/SendMessageModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  SEND_MESSAGE_WITHOUT_TARGET_MODAL: dynamic(() => import('./message/SendMessageWithoutTargetModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  POST_DETAIL_MODAL: dynamic(() => import('./message/PostDetailModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  RECEIVE_MESSAGE_SETTING_MODAL: dynamic(() => import('./message/ReceiveMessageSettingModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  REPORT_MODAL: dynamic(() => import('./report/ReportModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  RT_OPINION_MODAL: dynamic(() => import('./rtOpinion/RTOpinionModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  RESERVATIONCALENDER_MODAL: dynamic(() => import('./law/ReservationCalenderModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  BROADCAST_SHARE_MODAL: dynamic(() => import('./broadcast/share/BroadcastShareModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BROADCAST_SPON_MODAL: dynamic(() => import('./broadcast/spon/BroadcastSponModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  VOTE_MODAL: dynamic(() => import('./broadcast/vote/VoteModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  STORE_MODAL: dynamic(() => import('./store/StoreModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  PURCHASE_MODAL: dynamic(() => import('./purchase/PurchaseModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BANK_MODAL: dynamic(() => import('./purchase/BankModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BANK_RESULT_MODAL: dynamic(() => import('./purchase/BankResultModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  CASH_BILL_MODAL: dynamic(() => import('./purchase/CashBillModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  CASH_BILL_RECEIPT_DETAIL_MODAL: dynamic(() => import('./purchase/CashbillReceiptDetailModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  ARS_AUTH_MODAL: dynamic(() => import('./purchase/ArsAuthModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  ITEM_DETAIL_MODAL: dynamic(() => import('./item/ItemDetailModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  NICK_DECO_MODAL: dynamic(() => import('./item/NickDecoModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  INTRO_EFFECT_MODAL: dynamic(() => import('./item/IntroEffectModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  SEND_PUSH_MODAL: dynamic(() => import('./item/SendPushModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  FAN_GRADE_OPTION_MODAL: dynamic(() => import('./fan/FanGradeOptionModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  LIST_BACKGROUND_PREVIEW_MODAL: dynamic(() => import('./store/ListBackgroundPreviewModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  AUTH_MODAL: dynamic(() => import('./auth/AuthModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  IDENTITY_VERIFICATION_MODAL: dynamic(() => import('./auth/IdentityVerificationModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BANNER_MODAL: dynamic(() => import('./banner/BannerModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  PASSWORD_ENTRY_MODAL: dynamic(() => import('./play/PasswordEntryModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  SIGNATURE_EMOTICON_GUIDE_MODAL: dynamic(() => import('./guide/SignatureEmoticonGuideModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  SIGNATURE_EMOTICON_REGISTRATION_MODAL: dynamic(
    () => import('./signature/SignatureEmoticonRegistrationModalContainer'),
    {
      loading: () => <BaseSpinner />,
    },
  ),

  PROFILE_IMAGE_EDIT_MODAL: dynamic(() => import('./profile/ProfileImageEditModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  CHANNEL_EDIT_MODAL: dynamic(() => import('./channel/ChannelEditModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  CHANNEL_IMAGE_EDIT_MODAL: dynamic(() => import('./channel/ChannelImageEditModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  LIVE_PICK_MODAL: dynamic(() => import('./pick/LivePickModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BROADCAST_START_MODAL: dynamic(() => import('./broadcast/start/BroadcastStartModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BROADCAST_SETTING_GUIDE_MODAL: dynamic(() => import('./guide/BroadcastSettingGuideModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  TRANSMISSION_MODAL: dynamic(() => import('./guide/TransmissionsModal'), {
    loading: () => <BaseSpinner />,
  }),
  HELPER_WIDGET_GUIDE_MODAL: dynamic(() => import('./guide/HelperWidgetGuideModal'), {
    loading: () => <BaseSpinner />,
  }),

  NOTICE_WRITE_MODAL: dynamic(() => import('./channel/NoticeWriteModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  NOTICE_INFO_MODAL: dynamic(() => import('./channel/NoticeInfoModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  FAN_RANK_WRITE_MODAL: dynamic(() => import('./fan/FanRankWriteModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BLOCK_ADD_MODAL_CONTAINER: dynamic(() => import('./block/BlockAddModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  IMAGE_LIST_MODAL: dynamic(() => import('./image/ImageListModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  MARKETING_CONSENT_MODAL: dynamic(() => import('./marketing/MarketingConsentModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  ADD_COUPON_MODAL: dynamic(() => import('./coupon/AddCouponContainer'), {
    loading: () => <BaseSpinner />,
  }),
  AUTH_ACCOUNT_CHECK_MODAL: dynamic(() => import('./auth/AuthAccountCheckModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  PLAY_YOUTH_POLICY_MODAL: dynamic(() => import('./play/YouthPolicyContainer'), {
    loading: () => <BaseSpinner />,
  }),
  BROADCAST_RULE_MODAL: dynamic(() => import('./broadcast/start/BroadcastRuleModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  PRIVACY_COLLECT_POLICY_MODAL: dynamic(() => import('./auth/PrivacyCollectPolicyContainer'), {
    loading: () => <BaseSpinner />,
  }),
  PRIVACY_CONSIGN_POLICY_MODAL: dynamic(() => import('./auth/PrivacyConsignPolicyContainer'), {
    loading: () => <BaseSpinner />,
  }),

  NOTIFICATION_MODAL: dynamic(() => import('./notification/NotificationModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  NOTIFICATION_PER_BROWSER_RECEIVE_CONFIG_MODAL: dynamic(
    () => import('./notification/NotificationPerBrowserReceiveConfigModalContainer'),
    {
      loading: () => <BaseSpinner />,
    },
  ),

  ADULT_WARNING_MODAL: dynamic(() => import('./warning/AdultWarningModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  NOTIFICATION_TALK_SETTING_MODAL: dynamic(() => import('./notificationTalk/NotificationTalkSettingModalContainer'), {
    loading: () => <BaseSpinner />,
  }),

  RECAPTCHA_MODAL: dynamic(() => import('./auth/RecaptchaModal'), {
    loading: () => <BaseSpinner />,
  }),

  GLOBAL_SERVICE_PROFILE_EDIT_MODAL: dynamic(() => import('./globalService/GlobalProfileEditModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  GLOBAL_SERVICE_PROFILE_PREVIEW_MODAL: dynamic(() => import('./globalService/GlobalProfilePreviewModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
  GLOBAL_SERVICE_BROADCAST_AGREE_MODAL: dynamic(() => import('./globalService/GlobalBroadcastAgreeModalContainer'), {
    loading: () => <BaseSpinner />,
  }),
};

function ModalContainer() {
  const modalList = useModalStore((state) => state.modal);
  const isPlayerFullscreen = useOldPlayerStore((state) => state.isPlayerFullscreen);
  const { loginInfo } = useGetLoginInfoQuery();
  const scrollTop = useASPStore((s) => s.scrollTop);
  const fixedScrollTopRef = useRef<number | null>(null);

  // scrollTop이 처음 설정될 때만 저장
  if (scrollTop && fixedScrollTopRef.current === null) {
    fixedScrollTopRef.current = useASPStore.getState().scrollTop;
  }

  useEffect(() => {
    if (modalList.length > 0) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [modalList]);

  if (modalList.length === 0) return null;

  const onClickOutSideHandler = modalList[modalList.length - 1]?.onClickOutside;
  const render = modalList.map(({ type, options, content, id }, index, arr) => {
    const ModalComponent = MODAL_COMPONENTS[type as keyof typeof MODAL_TYPE_PC];
    const isIframeAsp = aspCond.if('iframe-asp');
    const hasMultipleModals = arr.length > 1;

    let center = hasMultipleModals;
    if (isIframeAsp) {
      center = hasMultipleModals && !scrollTop;
    }

    let position;
    if (isIframeAsp) {
      position = {
        top: (() => {
          if (scrollTop) {
            return `${(options?.purchaseData ? (fixedScrollTopRef?.current ?? 0) : scrollTop) + 250}px`;
          }
          return 'auto';
        })(),
      };
    }

    return (
      <React.Fragment key={index}>
        <div className='absolute h-full w-full bg-black/50' onClick={onClickOutSideHandler} />
        <div
          data-modal={id}
          className={cn('absolute', {
            '-translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2': center,
            'transition-all duration-300 ease-in-out': isIframeAsp,
          })}
          style={position}
        >
          {loginInfo?.loginInfo.console && (
            <div className='absolute top-[-10%] font-bold text-fuchsia-300'>
              <span>{id}</span>
              <span className='ml-2'>{SettingConfig.SHORT_SHA}</span>
            </div>
          )}
          {content || (ModalComponent && <ModalComponent {...options} />)}
        </div>
      </React.Fragment>
    );
  });

  const target = document.getElementById(isPlayerFullscreen ? 'player-portal' : 'portal');

  if (!target) return null;

  return createPortal(
    <>
      <div className='pointer-events-auto fixed inset-0 z-[45] grid place-items-center overflow-x-hidden'>{render}</div>
    </>,
    target,
  );
}

export default ModalContainer;
